export const STOREFRONT_SITE_HEADER = "X-Storefront-Site";
export const DEFAULT_STOREFRONT_SITE_CODE = "asorockfood";

const STOREFRONT_SITE_CODES = [
  DEFAULT_STOREFRONT_SITE_CODE,
  "asorockfrozen",
] as const;

export type StorefrontSiteCode = (typeof STOREFRONT_SITE_CODES)[number];

export type StorefrontTheme = {
  primary: string;
  primaryDark: string;
};

export type StorefrontSnipcartTheme = {
  accent: string;
  accentHover: string;
  badgeBackground: string;
  badgeActiveBackground: string;
  modalSurface: string;
  modalBackdrop: string;
  modalAltSurface: string;
  shellTextStrong: string;
  shellTextDefault: string;
  shellTextMuted: string;
  textStrong: string;
  textDefault: string;
  textMuted: string;
  border: string;
  inputBorder: string;
  inputFocus: string;
  inputAutofill: string;
  inputChecked: string;
  successBackground: string;
  buttonGradientStart: string;
  buttonGradientMid: string;
  buttonGradientEnd: string;
  buttonHoverStart: string;
  buttonHoverMid: string;
  buttonHoverEnd: string;
  buttonDisabled: string;
  panelBackground: string;
  panelBorder: string;
  panelShadow: string;
  sectionSurface: string;
  sectionSummaryBackground: string;
  sectionLabel: string;
  sectionValue: string;
  statusNeutral: string;
  statusSuccess: string;
  statusError: string;
  testBannerBackground: string;
  testBannerForeground: string;
  quantityButtonBackground: string;
  quantityButtonForeground: string;
};

export type StorefrontSnipcartCopy = {
  fulfilmentEyebrow: string;
  fulfilmentCopy: string;
  deliveryLabel: string;
  deliveryCaption: string;
  collectionLabel: string;
  collectionCaption: string;
  noDeliveryRatesMessage: string;
  noCollectionRatesMessage: string;
};

export type StorefrontSiteConfig = {
  code: StorefrontSiteCode;
  brandName: string;
  /** Meta-description fallback for pages whose CMS SEO description is empty. */
  defaultSeoDescription: string;
  desktopLogo: string;
  mobileLogo: string;
  inverseLogo: string;
  favicons: {
    ico: string;
    icon16: string;
    icon32: string;
    appleTouchIcon: string;
  };
  checkoutMode: "localDelivery" | "parcelDelivery";
  domains: string[];
  theme: StorefrontTheme;
  snipcart: {
    theme: StorefrontSnipcartTheme;
    copy: StorefrontSnipcartCopy;
  };
};

const asorockFoodTheme: StorefrontTheme = {
  primary: "#009639",
  primaryDark: "#002700",
};

const asorockFrozenTheme: StorefrontTheme = {
  primary: "#0d59f2",
  primaryDark: "#001f5e",
};

const storefrontSiteConfigByCode: Record<StorefrontSiteCode, StorefrontSiteConfig> = {
  asorockfood: {
    code: "asorockfood",
    brandName: "Aso Rock Food",
    defaultSeoDescription:
      "Nigerian restaurant in London serving jollof rice, egusi soup and ayamase. Dine in, collect or get same-day Nigerian food delivery across London.",
    desktopLogo: "/logo-desktop.svg",
    mobileLogo: "/logo-mobile.svg",
    inverseLogo: "/logo-desktop-inverse.svg",
    favicons: {
      ico: "/favicons/asorockfood/favicon.ico",
      icon16: "/favicons/asorockfood/favicon-16x16.png",
      icon32: "/favicons/asorockfood/favicon-32x32.png",
      appleTouchIcon: "/favicons/asorockfood/apple-touch-icon.png",
    },
    checkoutMode: "localDelivery",
    domains: [
      "asorockfood.com",
      "www.asorockfood.com",
      "asorockfood.localhost",
    ],
    theme: asorockFoodTheme,
    snipcart: {
      theme: buildStorefrontSnipcartTheme(asorockFoodTheme, {
        shellSurface: asorockFoodTheme.primaryDark,
      }),
      copy: {
        fulfilmentEyebrow: "Fulfilment",
        fulfilmentCopy: "Choose delivery or collection before selecting a time slot.",
        deliveryLabel: "Delivery",
        deliveryCaption: "Courier delivery to your address",
        collectionLabel: "Collection",
        collectionCaption: "Pickup from the store",
        noDeliveryRatesMessage: "No delivery times are available right now.",
        noCollectionRatesMessage: "No collection times are available right now.",
      },
    },
  },
  asorockfrozen: {
    code: "asorockfrozen",
    brandName: "Aso Rock Frozen",
    defaultSeoDescription:
      "Buy authentic Nigerian food online — jollof rice, egusi, ayamase and more, flash-frozen by Aso Rock and delivered UK-wide with flat-rate shipping.",
    desktopLogo: "/logo-desktop-frozen.svg",
    mobileLogo: "/logo-mobile-frozen.svg",
    inverseLogo: "/logo-desktop-frozen-inverse.svg",
    favicons: {
      ico: "/favicons/asorockfrozen/favicon.ico",
      icon16: "/favicons/asorockfrozen/favicon-16x16.png",
      icon32: "/favicons/asorockfrozen/favicon-32x32.png",
      appleTouchIcon: "/favicons/asorockfrozen/apple-touch-icon.png",
    },
    checkoutMode: "parcelDelivery",
    domains: [
      "asorockfrozen.com",
      "www.asorockfrozen.com",
      "asorockfrozen.localhost",
    ],
    theme: asorockFrozenTheme,
    snipcart: {
      theme: buildStorefrontSnipcartTheme(asorockFrozenTheme, {
        shellSurface: "#1e3a70",
      }),
      copy: {
        fulfilmentEyebrow: "Fulfilment",
        fulfilmentCopy:
          "Choose shipping or collection before selecting a dispatch option.",
        deliveryLabel: "Shipping",
        deliveryCaption: "Nationwide courier delivery to your address",
        collectionLabel: "Collection",
        collectionCaption: "Pickup from the store",
        noDeliveryRatesMessage: "No shipping options are available right now.",
        noCollectionRatesMessage: "No collection slots are available right now.",
      },
    },
  },
};

export function isStorefrontSiteCode(
  value: string | null | undefined,
): value is StorefrontSiteCode {
  return STOREFRONT_SITE_CODES.includes(value as StorefrontSiteCode);
}

export function normalizeStorefrontSiteCode(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const normalizedValue = value.trim().toLowerCase();
  return isStorefrontSiteCode(normalizedValue) ? normalizedValue : null;
}

export function getStorefrontSiteConfig(
  siteCode: StorefrontSiteCode = DEFAULT_STOREFRONT_SITE_CODE,
) {
  return storefrontSiteConfigByCode[siteCode];
}

/**
 * The absolute origin (`https://www....`) used for canonical URLs, OG tags and
 * structured data. Derived from the site's configured `www.` domain.
 */
export function getStorefrontCanonicalOrigin(
  siteCode: StorefrontSiteCode = DEFAULT_STOREFRONT_SITE_CODE,
) {
  const { domains } = getStorefrontSiteConfig(siteCode);
  const canonicalHost =
    domains.find((domain) => domain.startsWith("www.")) ?? domains[0];

  return `https://${canonicalHost}`;
}

/**
 * True only for the real public storefront hostnames (apex or www of the
 * production domains). Everything else — App Service default hostnames
 * (`*.azurewebsites.net`, staging and prod alike), localhost dev hosts,
 * previews — is non-canonical: the same content lives on the canonical
 * domains, so non-canonical hosts must never be indexed by search engines.
 */
export function isCanonicalStorefrontHost(hostname: string | null | undefined) {
  const normalizedHostname = normalizeHostname(hostname);
  if (!normalizedHostname) {
    return false;
  }

  return STOREFRONT_SITE_CODES.some((siteCode) =>
    storefrontSiteConfigByCode[siteCode].domains.some(
      (domain) =>
        !domain.endsWith(".localhost") &&
        normalizeHostname(domain) === normalizedHostname,
    ),
  );
}

export function resolveStorefrontSiteCode({
  configuredSiteCode,
  defaultSiteCode = DEFAULT_STOREFRONT_SITE_CODE,
  hostname,
}: {
  configuredSiteCode?: string | null;
  defaultSiteCode?: StorefrontSiteCode;
  hostname?: string | null;
} = {}) {
  const explicitSiteCode = normalizeStorefrontSiteCode(configuredSiteCode);
  if (explicitSiteCode) {
    return explicitSiteCode;
  }

  const localhostPortSiteCode = resolveLocalhostPortSiteCode(hostname);
  if (localhostPortSiteCode) {
    return localhostPortSiteCode;
  }

  const normalizedHostname = normalizeHostname(hostname);
  if (!normalizedHostname) {
    return defaultSiteCode;
  }

  const matchingSite = STOREFRONT_SITE_CODES.find((siteCode) => {
    const config = storefrontSiteConfigByCode[siteCode];
    return config.domains.some((domain) => {
      const normalizedDomain = normalizeHostname(domain);
      return Boolean(
        normalizedDomain &&
          (normalizedHostname === normalizedDomain ||
            normalizedHostname.endsWith(`.${normalizedDomain}`)),
      );
    });
  });

  if (matchingSite) {
    return matchingSite;
  }

  if (normalizedHostname.includes("frozen")) {
    return "asorockfrozen";
  }

  if (normalizedHostname.includes("food")) {
    return "asorockfood";
  }

  return defaultSiteCode;
}

export function buildStorefrontThemeVariables(
  siteCode: StorefrontSiteCode = DEFAULT_STOREFRONT_SITE_CODE,
) {
  const site = getStorefrontSiteConfig(siteCode);
  const { primary, primaryDark } = site.theme;

  return {
    "--storefront-primary": primary,
    "--storefront-primary-dark": primaryDark,
    "--storefront-primary-rgb": toRgbValue(primary),
    "--storefront-primary-dark-rgb": toRgbValue(primaryDark),
    "--storefront-primary-soft": toAlpha(primary, 0.1),
    "--storefront-primary-soft-strong": toAlpha(primary, 0.18),
    "--storefront-primary-ring": toAlpha(primary, 0.22),
    "--storefront-primary-tint": mixHex(primary, "#ffffff", 0.62),
    "--storefront-primary-deep": darkenHex(primaryDark, 0.3),
    "--storefront-shell-background": `radial-gradient(circle_at_top_left, rgba(255, 255, 255, 0.12), transparent 24%), linear-gradient(180deg, ${darkenHex(primaryDark, 0.08)} 0%, ${primaryDark} 26%, ${darkenHex(primaryDark, 0.4)} 100%)`,
    "--storefront-footer-background": primary,
    "--storefront-breadcrumb-background": toAlpha(primaryDark, 0.88),
  } as Record<string, string>;
}

export function buildStorefrontSnipcartVariables(
  siteCode: StorefrontSiteCode = DEFAULT_STOREFRONT_SITE_CODE,
) {
  const theme = getStorefrontSiteConfig(siteCode).snipcart.theme;

  return {
    "--rl-snipcart-accent": theme.accent,
    "--rl-snipcart-accent-hover": theme.accentHover,
    "--rl-snipcart-badge-background": theme.badgeBackground,
    "--rl-snipcart-badge-active-background": theme.badgeActiveBackground,
    "--rl-snipcart-modal-surface": theme.modalSurface,
    "--rl-snipcart-modal-backdrop": theme.modalBackdrop,
    "--rl-snipcart-modal-alt-surface": theme.modalAltSurface,
    "--rl-snipcart-shell-text-strong": theme.shellTextStrong,
    "--rl-snipcart-shell-text-default": theme.shellTextDefault,
    "--rl-snipcart-shell-text-muted": theme.shellTextMuted,
    "--rl-snipcart-text-strong": theme.textStrong,
    "--rl-snipcart-text-default": theme.textDefault,
    "--rl-snipcart-text-muted": theme.textMuted,
    "--rl-snipcart-border": theme.border,
    "--rl-snipcart-input-border": theme.inputBorder,
    "--rl-snipcart-input-focus": theme.inputFocus,
    "--rl-snipcart-input-autofill": theme.inputAutofill,
    "--rl-snipcart-input-checked": theme.inputChecked,
    "--rl-snipcart-success-background": theme.successBackground,
    "--rl-snipcart-button-gradient-start": theme.buttonGradientStart,
    "--rl-snipcart-button-gradient-mid": theme.buttonGradientMid,
    "--rl-snipcart-button-gradient-end": theme.buttonGradientEnd,
    "--rl-snipcart-button-hover-start": theme.buttonHoverStart,
    "--rl-snipcart-button-hover-mid": theme.buttonHoverMid,
    "--rl-snipcart-button-hover-end": theme.buttonHoverEnd,
    "--rl-snipcart-button-disabled": theme.buttonDisabled,
    "--rl-snipcart-panel-background": theme.panelBackground,
    "--rl-snipcart-panel-border": theme.panelBorder,
    "--rl-snipcart-panel-shadow": theme.panelShadow,
    "--rl-snipcart-section-surface": theme.sectionSurface,
    "--rl-snipcart-section-summary-background": theme.sectionSummaryBackground,
    "--rl-snipcart-section-label": theme.sectionLabel,
    "--rl-snipcart-section-value": theme.sectionValue,
    "--rl-snipcart-status-neutral": theme.statusNeutral,
    "--rl-snipcart-status-success": theme.statusSuccess,
    "--rl-snipcart-status-error": theme.statusError,
    "--rl-snipcart-test-banner-background": theme.testBannerBackground,
    "--rl-snipcart-test-banner-foreground": theme.testBannerForeground,
    "--rl-snipcart-quantity-button-background": theme.quantityButtonBackground,
    "--rl-snipcart-quantity-button-foreground": theme.quantityButtonForeground,
  } as Record<string, string>;
}

function normalizeHostname(hostname: string | null | undefined) {
  if (!hostname) {
    return null;
  }

  // Forwarded-host headers can carry a comma-separated hop list — the first
  // entry is the original client-facing host.
  const value = hostname
    .split(",")[0]
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/\.$/, "");
  const hostnameOnly = value.includes(":") ? value.split(":")[0] : value;

  return hostnameOnly || null;
}

function resolveLocalhostPortSiteCode(hostname: string | null | undefined) {
  if (!hostname) {
    return null;
  }

  const value = hostname
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/\.$/, "");
  const [hostnameOnly, port] = value.split(":");

  if (hostnameOnly !== "localhost" && hostnameOnly !== "127.0.0.1") {
    return null;
  }

  if (port === "3001") {
    return "asorockfrozen";
  }

  if (port === "3000") {
    return "asorockfood";
  }

  return null;
}

function buildStorefrontSnipcartTheme(
  theme: StorefrontTheme,
  options: { shellSurface?: string } = {},
): StorefrontSnipcartTheme {
  const { primary, primaryDark } = theme;
  const accent = darkenHex(primary, 0.08);
  const shellSurface = options.shellSurface ?? primaryDark;

  return {
    accent,
    accentHover: darkenHex(primary, 0.2),
    badgeBackground: shellSurface,
    badgeActiveBackground: accent,
    modalSurface: shellSurface,
    modalBackdrop: toAlpha(shellSurface, 0.56),
    modalAltSurface: mixHex(shellSurface, "#ffffff", 0.08),
    shellTextStrong: "#ffffff",
    shellTextDefault: toAlpha("#ffffff", 0.94),
    shellTextMuted: toAlpha("#ffffff", 0.72),
    textStrong: "#000000",
    textDefault: mixHex(primaryDark, "#1f2937", 0.18),
    textMuted: mixHex(primaryDark, "#94a3b8", 0.58),
    border: toAlpha("#ffffff", 0.14),
    inputBorder: toAlpha(primaryDark, 0.22),
    inputFocus: accent,
    inputAutofill: toAlpha(primary, 0.08),
    inputChecked: accent,
    successBackground: toAlpha(primary, 0.12),
    buttonGradientStart: primary,
    buttonGradientMid: accent,
    buttonGradientEnd: mixHex(primaryDark, "#000000", 0.18),
    buttonHoverStart: darkenHex(primary, 0.14),
    buttonHoverMid: darkenHex(primary, 0.22),
    buttonHoverEnd: mixHex(primaryDark, "#000000", 0.28),
    buttonDisabled: mixHex(primary, "#ffffff", 0.42),
    panelBackground: "#ffffff",
    panelBorder: toAlpha(primaryDark, 0.1),
    panelShadow: `0 20px 40px -34px ${toAlpha(primaryDark, 0.55)}`,
    sectionSurface: "#ffffff",
    sectionSummaryBackground: toAlpha(primary, 0.08),
    sectionLabel: darkenHex(primary, 0.14),
    sectionValue: mixHex(primaryDark, primary, 0.18),
    statusNeutral: mixHex(primaryDark, "#64748b", 0.66),
    statusSuccess: darkenHex(primary, 0.12),
    statusError: "#b91c1c",
    testBannerBackground: primary,
    testBannerForeground: "#ffffff",
    quantityButtonBackground: shellSurface,
    quantityButtonForeground: "#ffffff",
  };
}

function toRgbValue(hex: string) {
  const [r, g, b] = parseHexColor(hex);
  return `${r}, ${g}, ${b}`;
}

function toAlpha(hex: string, opacity: number) {
  const [r, g, b] = parseHexColor(hex);
  return `rgba(${r}, ${g}, ${b}, ${clamp(opacity, 0, 1)})`;
}

function darkenHex(hex: string, amount: number) {
  return mixHex(hex, "#000000", amount);
}

function mixHex(sourceHex: string, mixHexValue: string, amount: number) {
  const source = parseHexColor(sourceHex);
  const mix = parseHexColor(mixHexValue);
  const ratio = clamp(amount, 0, 1);

  return toHexColor(
    source.map((channel, index) =>
      Math.round(channel + (mix[index] - channel) * ratio),
    ) as [number, number, number],
  );
}

function parseHexColor(hex: string): [number, number, number] {
  const normalizedHex = hex.trim().replace(/^#/, "");
  const expandedHex =
    normalizedHex.length === 3
      ? normalizedHex
          .split("")
          .map((segment) => `${segment}${segment}`)
          .join("")
      : normalizedHex;

  if (!/^[0-9a-f]{6}$/i.test(expandedHex)) {
    throw new Error(`Invalid hex colour: ${hex}`);
  }

  return [
    Number.parseInt(expandedHex.slice(0, 2), 16),
    Number.parseInt(expandedHex.slice(2, 4), 16),
    Number.parseInt(expandedHex.slice(4, 6), 16),
  ];
}

function toHexColor([r, g, b]: [number, number, number]) {
  return `#${[r, g, b]
    .map((channel) => clamp(Math.round(channel), 0, 255).toString(16).padStart(2, "0"))
    .join("")}`;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
